import { Command } from "cmdk";
import { useEffect, useMemo } from "react";
import { useCockpit } from "../../lib/state";
import {
  useProjects,
  useServices,
  useTasks,
} from "../../lib/queries";
import { buildCommandItems } from "../../lib/commands";

interface CommandPaletteProps {
  onNewTask: () => void;
  onFocusEditor: () => void;
}

export default function CommandPalette({ onNewTask, onFocusEditor }: CommandPaletteProps) {
  const open = useCockpit((s) => s.paletteOpen);
  const setPaletteOpen = useCockpit((s) => s.setPaletteOpen);
  const setSelected = useCockpit((s) => s.setSelectedProjectId);
  const openDrawer = useCockpit((s) => s.openDrawer);
  const setHelpOpen = useCockpit((s) => s.setHelpOpen);

  const { data: projects = [] } = useProjects();
  const { data: tasks = [] } = useTasks();
  const { data: services = [] } = useServices();

  const items = useMemo(
    () =>
      buildCommandItems({
        projects,
        tasks,
        services,
        onPickProject: (id) => {
          setSelected(id);
          setPaletteOpen(false);
        },
        onPickTask: (id) => {
          openDrawer("task", id);
          setPaletteOpen(false);
        },
        onPickService: (id) => {
          openDrawer("service", id);
          setPaletteOpen(false);
        },
        onNewTask: () => {
          setPaletteOpen(false);
          onNewTask();
        },
        onFocusEditor: () => {
          setPaletteOpen(false);
          onFocusEditor();
        },
        onShowHelp: () => {
          setPaletteOpen(false);
          setHelpOpen(true);
        },
      }),
    [
      projects,
      tasks,
      services,
      setSelected,
      setPaletteOpen,
      openDrawer,
      onNewTask,
      onFocusEditor,
      setHelpOpen,
    ],
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPaletteOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, setPaletteOpen]);

  if (!open) return null;

  const groups: Array<"Projects" | "Tasks" | "Services" | "Actions"> = [
    "Actions",
    "Projects",
    "Tasks",
    "Services",
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center"
      style={{
        paddingTop: "12vh",
        background:
          "radial-gradient(ellipse at center, rgba(255,31,165,0.18), rgba(74,19,66,0.45))",
        backdropFilter: "blur(8px)",
      }}
      onClick={() => setPaletteOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="aero-glass aero-glass-strong w-full max-w-xl overflow-hidden"
        style={{ borderRadius: 28 }}
      >
        <div
          className="px-5 pt-4 pb-2"
          style={{ borderBottom: "1px solid rgba(255,31,165,0.12)" }}
        >
          <span
            className="font-eyebrow"
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "var(--hot-pink)",
            }}
          >
            ✦ Command palette
          </span>
          <h2
            className="mt-1 text-2xl"
            style={{
              fontFamily: "Fraunces, serif",
              fontStyle: "italic",
              fontWeight: 700,
              letterSpacing: "-0.01em",
            }}
          >
            <span className="holo-text">Where to, gorgeous?</span>
          </h2>
        </div>
        <Command label="Command Palette">
          <Command.Input placeholder="Search projects, tasks, services, actions…" autoFocus />
          <Command.List>
            <Command.Empty>No results — try another word, queen.</Command.Empty>
            {groups.map((g) => {
              const inGroup = items.filter((i) => i.group === g);
              if (!inGroup.length) return null;
              return (
                <Command.Group key={g} heading={g}>
                  {inGroup.map((i) => (
                    <Command.Item
                      key={i.id}
                      value={`${i.label} ${i.hint ?? ""}`}
                      onSelect={i.onSelect}
                    >
                      <span
                        className="flex-1 truncate"
                        style={{
                          fontFamily: "Fraunces, serif",
                          fontStyle: "italic",
                          fontSize: 14,
                        }}
                      >
                        {i.label}
                      </span>
                      {i.hint ? (
                        <span
                          className="font-eyebrow"
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: "0.14em",
                            color: "var(--hot-pink)",
                            opacity: 0.7,
                          }}
                        >
                          {i.hint}
                        </span>
                      ) : null}
                    </Command.Item>
                  ))}
                </Command.Group>
              );
            })}
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
